import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Form,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import CustomSelectDropdown from 'components/shared/DropDown/CustomSelectDropdown';
import axiosPrivate from 'config/axiosPrivate';
import {
  TARGET_TRANSLATION_LANGUAGES,
  filterAgentsByTargetLanguage,
  getAgentLanguageLabel,
} from 'utils/smartContent/translationLanguageUtils';
import './SmartContentPopintrad.css';

const resolveAgentId = (agent) => {
  if (!agent) return '';
  return String(agent.id ?? agent.agentId ?? agent._id ?? '').trim();
};

const renderLanguageFlag = (language) => {
  if (!language) return null;

  if (language.img) {
    return (
      <img
        src={language.img}
        alt=""
        className="translate-content-language-flag"
        aria-hidden="true"
      />
    );
  }

  return (
    <span className="translate-content-language-emoji" aria-hidden="true">
      {language.flagEmoji || '🌐'}
    </span>
  );
};

const TranslateContentPopin = ({
  isOpen,
  onClose,
  websiteUrl = '',
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [targetLanguage, setTargetLanguage] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [translationAgents, setTranslationAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [isAgentDropdownOpen, setIsAgentDropdownOpen] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setTargetLanguage(null);
      setSelectedAgent(null);
      setIsAgentDropdownOpen(false);
      setSubmitError('');
      return;
    }

    let isMounted = true;

    const fetchAgents = async () => {
      if (!websiteUrl) {
        if (isMounted) {
          setTranslationAgents([]);
        }
        return;
      }

      setLoadingAgents(true);
      try {
        const response = await axiosPrivate.get(
          `/smart-content/agent_ia/traduction/all?websiteUrl=${encodeURIComponent(websiteUrl)}`
        );
        if (!isMounted) return;
        const agents = (response.data || []).map((agent) => ({
          ...agent,
          displayName: agent.name || agent.agentName || '',
        }));
        setTranslationAgents(agents);
      } catch (error) {
        console.error('Erreur lors du chargement des agents de traduction:', error);
        if (isMounted) {
          setTranslationAgents([]);
        }
      } finally {
        if (isMounted) {
          setLoadingAgents(false);
        }
      }
    };

    fetchAgents();

    return () => {
      isMounted = false;
    };
  }, [isOpen, websiteUrl]);

  const filteredAgents = useMemo(
    () => filterAgentsByTargetLanguage(translationAgents, targetLanguage),
    [translationAgents, targetLanguage]
  );

  useEffect(() => {
    if (!targetLanguage || loadingAgents) {
      if (!loadingAgents && targetLanguage) {
        setSelectedAgent(null);
      }
      return;
    }

    if (filteredAgents.length === 0) {
      setSelectedAgent(null);
      return;
    }

    setSelectedAgent((current) => {
      if (!current) return filteredAgents[0];
      const currentId = resolveAgentId(current);
      const stillAvailable = filteredAgents.some(
        (agent) => resolveAgentId(agent) === currentId
      );
      return stillAvailable ? current : filteredAgents[0];
    });
  }, [filteredAgents, loadingAgents, targetLanguage]);

  const handleTranslate = () => {
    setSubmitError('');

    if (!targetLanguage) {
      setSubmitError(t('Sélectionnez une langue cible.'));
      return;
    }

    const agent = selectedAgent || filteredAgents[0];
    if (!agent) {
      setSubmitError(t('Aucun agent disponible pour cette langue.'));
      return;
    }

    if (typeof onSubmit === 'function') {
      onSubmit({
        targetLanguage,
        agent,
        agentId: resolveAgentId(agent),
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="md"
      className={`translation-popin translate-content-popin${isAgentDropdownOpen ? ' agent-dropdown-open' : ''}`}
    >
      <div className="translate-content-popin-header">
        <div className="translate-content-popin-header-top">
          <div className="translate-content-popin-title-row">
            <span className="translate-content-popin-icon-wrap" aria-hidden="true">
              <img
                src="/Images/Typix_Traduction-selection.png"
                alt=""
                className="translate-content-popin-icon"
              />
            </span>
            <h2 className="translate-content-popin-title">{t('Traduire le texte')}</h2>
          </div>
          <button
            type="button"
            className="translate-content-popin-close"
            onClick={onClose}
            aria-label={t('Fermer')}
          >
            ×
          </button>
        </div>
      </div>

      <Form onSubmit={(event) => {
        event.preventDefault();
        handleTranslate();
      }}>
        <ModalBody>
          <div className="smart-content-popin-section">
            {submitError ? (
              <p className="translate-content-popin-error" role="alert">
                {submitError}
              </p>
            ) : null}
            <FormGroup>
              <Label htmlFor="translateTargetLanguage" className="smart-content-popin-label">
                {t('Langue cible')}
                {' '}
                <span className="smart-content-popin-required">*</span>
              </Label>
              <div
                id="translateTargetLanguage"
                className="translate-content-language-list"
                role="listbox"
                aria-label={t('Langue cible')}
              >
              {TARGET_TRANSLATION_LANGUAGES.map((language) => {
                const isSelected = targetLanguage?.isoCode === language.isoCode;

                return (
                  <button
                    key={language.isoCode}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={`translate-content-language-card${isSelected ? ' is-selected' : ''}`}
                    onClick={() => setTargetLanguage(language)}
                  >
                    <span className="translate-content-language-card-left">
                      {renderLanguageFlag(language)}
                      <span className="translate-content-language-card-name">
                        {t(language.language)}
                      </span>
                    </span>
                    {isSelected && (
                      <span className="translate-content-language-card-check" aria-hidden="true">
                        ✓
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            </FormGroup>

            <FormGroup className="translate-content-agent-group">
              <Label htmlFor="translateAgent" className="smart-content-popin-label">
                {t('Agent de traduction')}
                {' '}
                <span className="smart-content-popin-required">*</span>
              </Label>
              <CustomSelectDropdown
                options={filteredAgents}
                value={selectedAgent}
                onChange={setSelectedAgent}
                disabled={!targetLanguage || loadingAgents || filteredAgents.length === 0}
                menuInline
                hideDropdownIcon={
                  Boolean(targetLanguage) && !loadingAgents && filteredAgents.length === 0
                }
                onMenuOpen={() => setIsAgentDropdownOpen(true)}
                onMenuClose={() => setIsAgentDropdownOpen(false)}
                getOptionLabel={(agent) => agent?.displayName || agent?.name || agent?.agentName || t('Agent IA')}
                getOptionValue={(agent) => resolveAgentId(agent)}
                renderOptionLabel={(agent) => {
                  if (!agent) return t('Agent IA');
                  const name = agent.displayName || agent.name || agent.agentName || t('Agent IA');
                  const lang = getAgentLanguageLabel(agent);
                  return (
                    <span className="translate-content-agent-option">
                      <span>{name}</span>
                      {lang ? <span className="translate-content-agent-lang">{lang}</span> : null}
                    </span>
                  );
                }}
                placeholder={
                  !targetLanguage
                    ? t('Sélectionnez d’abord une langue')
                    : loadingAgents
                      ? t('Chargement...')
                      : filteredAgents.length > 0
                        ? t('Sélectionnez un agent')
                        : t('Aucun agent disponible pour cette langue')
                }
                className="smart-content-popin-input smart-content-popin-agent-dropdown"
              />
            </FormGroup>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            className="smart-content-popin-cancel"
            onClick={onClose}
          >
            {t('Annuler')}
          </Button>
          <button
            type="button"
            className="smart-content-popin-button translate-content-popin-submit"
            onClick={handleTranslate}
          >
            {t('Traduire')}
          </button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default TranslateContentPopin;
